import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { confirmBooking, getMeetingDetails, getOrgDetails } from '../../../helper/api.helper';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Loader from '../../../components/loader/Loader';
import PaymentMethod from './payment-method/PaymentMethod';
import BillingDetails from './billing-details/BillingDetails';
import OrderSummary from './order-summary/OrderSummary';
import { useStoreState } from 'easy-peasy';
import { getOrgCode } from '../../../helper/utils.helper';
import { auth } from '../../../helper/firebase.helper';

const Checkout = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const timestamp = queryParams.get('date');
  const selectedDate = timestamp ? new Date(parseInt(timestamp)) : null;
  const selectedEventType = queryParams.get('eventType');
  const selectedLocation = queryParams.get('location');
  let selectedTime = null;
  const timeParam = queryParams.get('time');
  try {
    selectedTime = timeParam ? JSON.parse(decodeURIComponent(timeParam)) : null;
  } catch (error) {
    console.error('Error parsing selectedTime:', error);
  }
  const [billingDetails, setBillingDetails] = useState({
    paymentMethod: 0,
    name: '',
    email: auth?.currentUser?.email || '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  });
  const [meetingDetails, setMeetingDetails] = useState({});
  const [mobile, setMobile] = useState(auth?.currentUser?.phoneNumber || '');

  const [isLoading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    const orgCode = getOrgCode();
    getMeetingDetails(orgCode, id, selectedEventType, selectedLocation)
      .then((res) => res.json())
      .then((res) => {
        if (res.status === 'success') {
          setMeetingDetails(res.data);
          if (res?.data?.client?.address) {
            setBillingDetails((prev) => ({
              ...prev,
              name: res?.data?.client?.name || '',
              street1: res?.data?.client?.address?.street1 || '',
              street2: res?.data?.client?.address?.street2 || '',
              city: res?.data?.client?.address?.city || '',
              state: res?.data?.client?.address?.state || '',
              country: res?.data?.client?.address?.country || '',
              postalCode: res?.data?.client?.address?.postalCode || ''
            }));
            setMobile(res.data?.client?.mobile);
          }
          if (res?.data?.paymentTypes) {
            setPaymentMethods(res?.data?.paymentTypes);
            if (res?.data?.paymentTypes?.includes(1)) {
              setBillingDetails((prev) => ({ ...prev, paymentMethod: 1 }));
            }
          }
        } else {
          toast.error(res.message);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err.message);
      });
  }, []);

  const handleChange = (field, value) => {
    setBillingDetails((prev) => ({ ...prev, [field]: value }));
  };
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'postalCode' && value.length > 6) {
      toast.error('Postal code should be of 6 numbers');
      return;
    }
    handleChange(name, value);
  };

  const handleBook = async () => {
    const orgCode = getOrgCode();
    setLoading(true);
    await confirmBooking(orgCode, id, {
      serviceId: selectedEventType,
      locationId: selectedLocation,
      selectedDate: selectedDate.getTime(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      selectedTimeSlot: selectedTime,
      name: billingDetails.name,
      email: billingDetails.email,
      mobile:
        mobile && mobile !== '91' && mobile.startsWith('+')
          ? mobile
          : mobile && mobile !== '91' && !mobile.startsWith('+')
            ? `+${mobile}`
            : null,
      address: {
        street1: billingDetails.street1,
        street2: billingDetails.street2,
        city: billingDetails.city,
        state: billingDetails.state,
        country: billingDetails.country,
        postalCode: billingDetails.postalCode
      },
      ...(Number(meetingDetails?.service?.price) !== 0 && {
        paymentMethod: billingDetails.paymentMethod
      })
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.status === 'success') {
          if (res?.orderId) {
            openRazorpay(res.orderId);
            setLoading(false);
          } else {
            setLoading(false);
            toast.success('Session booked successfully');
            navigate(`/therapists/${id}/checkout/payment-successful`);
          }
        } else {
          setLoading(false);
          toast.error(res.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err.message);
      });
  };

  const openRazorpay = (order_id) => {
    const options = {
      // eslint-disable-next-line no-undef
      key: process.env.REACT_APP_RZP_API_KEY,
      order_id: order_id,
      handler: function () {
        toast.success('Session booked successfully');
        navigate(`/therapists/${id}/checkout/payment-successful`);
      },
      prefill: {
        name: billingDetails.name,
        email: billingDetails.email,
        contact: mobile
      },
      theme: {
        color: '#3399cc'
      }
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.on('payment.failed', function (response) {
      // alert(response.error.code);
      toast.error(`Payment failed: ${response.error.description}.`);
      // alert(response.error.source);
      // alert(response.error.step);
      // alert(response.error.reason);
      // alert(response.error.metadata.order_id);
      // alert(response.error.metadata.payment_id);
    });
    rzp1.open();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // openRazorpay();
    if (billingDetails.postalCode.length !== 6) {
      toast.error('Postal code should be of 6 numbers');
      return;
    }
    if (!billingDetails.city) {
      toast.error('Please select city.');
      return;
    }
    // if (mobile?.length !== 12) {
    //   toast.error('Mobile no. should be of 10 numbers');
    //   return;
    // }
    handleBook();
  };

  return (
    <div className="checkout-wrapper flex-container">
      {isLoading ? <Loader /> : null}

      <div className="fields-wrapper flex-container ">
        <div className="fields-container flex-container direction-column">
          {Number(meetingDetails?.service?.price) === 0 ? null : (
            <PaymentMethod
              billingDetails={billingDetails}
              paymentMethods={paymentMethods}
              handleInputChange={handleInputChange}
            />
          )}
          <BillingDetails
            handleSubmit={handleSubmit}
            handleInputChange={handleInputChange}
            billingDetails={billingDetails}
            mobile={mobile}
            setMobile={setMobile}
            setBillingDetails={setBillingDetails}
          />
        </div>
      </div>
      <OrderSummary
        price={meetingDetails?.service?.price}
        selectedTime={selectedTime}
        selectedDate={selectedDate}
        meetingDetails={meetingDetails}
      />
      <style jsx>{`
        .checkout-wrapper {
          margin: auto;
          max-width: 1000px;
          min-height: 87vh;
          padding: 20px;
        }
        .fields-wrapper {
          width: 70%;
        }
        .fields-container {
          max-width: 500px;
        }

        @media only screen and (max-width: 800px) {
          .checkout-wrapper {
            flex-direction: column;
            align-items: center;
          }
          .fields-wrapper {
            justify-content: center;
            margin-top: 20px;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Checkout;
